import React, { useId } from "react";
import styled from "styled-components";
import CardHeader from "./CardHeader";

// #region SCOPED CSS
const StyledCard = styled.section`
    && {
        box-sizing: border-box;
        background: #17191b;
        border: 1px solid #34393a;
        border-radius: 12px;
        overflow: hidden;
        min-width: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
    }
`;

const StyledBody = styled.div`
    box-sizing: border-box;
    min-width: 0;
    flex: 1 1 auto;
    padding: ${({ $bodyPadding }) => $bodyPadding ? "24px" : "0"};

    @media (max-width: 600px) {
        padding: ${({ $bodyPadding }) => $bodyPadding ? "24px 16px" : "0"};
    }
`;
// #endregion

/**
 * Presentation-only Sales Intel card surface with the shared premium header.
 * @param {Object} props
 * @param {string} props.title Required card title.
 * @param {React.ReactNode} [props.children] Arbitrary body content.
 * @param {"h1"|"h2"|"h3"} [props.headingLevel="h2"] Semantic heading level.
 * @param {string} [props.titleId] Heading ID; generated with useId when omitted.
 * @param {React.ReactNode} [props.rightContent] Optional header content.
 * @param {React.ReactNode} [props.afterTitleComponent] Content adjacent to the title.
 * @param {boolean} [props.headerWrap=false] Allow the header to grow and wrap.
 * @param {Object} [props.style] Inline section styles.
 * @param {string} [props.className] Class applied to the section.
 * @param {string} [props.bodyClassName] Class applied to the body.
 * @param {boolean} [props.bodyPadding=true] Apply responsive content padding.
 */
export default function Card({
    title, children, headingLevel = "h2", titleId, rightContent, className,
    bodyClassName, bodyPadding = true, afterTitleComponent, headerWrap = false, style
}) {
    const generatedTitleId = useId();
    const headingId = titleId ?? generatedTitleId;

    return (
        <StyledCard style={style} className={className} aria-labelledby={headingId}>
            <CardHeader title={title} headingLevel={headingLevel} titleId={headingId} rightContent={rightContent}
                afterTitleComponent={afterTitleComponent} wrap={headerWrap} />
            <StyledBody className={bodyClassName} $bodyPadding={bodyPadding}>{children}</StyledBody>
        </StyledCard>
    );
}
